import React, { useEffect, useState } from "react";
import { Box, GlobalStyles } from "@mui/material";
import ServicesByTechnicianDetailedChart from "../components/ServicesByTechnicianDetailedChart";
import CockroachGame from "../components/CockroachGame";
import PestMemorama from "../components/PestMemorama";
import CockroachRanking from "../components/CockroachRanking";
import PestHotBranches from "../components/PestHotBranches";
import { THEME_COLORS } from "../utils/constants";
import { useAuth } from "../context/authContext";
import { hasRoleAccess, ACCESS } from "../utils/rolePermissions";
import gameScoreService from "../api/gameScoreService";

function mexicoHour() {
  const hour = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Mexico_City",
    hour: "numeric",
    hourCycle: "h23",
  })
    .formatToParts(new Date())
    .find((part) => part.type === "hour")?.value;
  return Number(hour);
}

function scheduledGame() {
  const hour = mexicoHour();
  return hour >= 8 && hour < 20 ? "memorama" : "cockroach";
}

const SWITCH_STYLES = `
  .gsw {
    display: flex;
    gap: 4px;
    padding: 4px;
    background: #ffffff;
    border-radius: 12px;
    border: 1.5px solid #e4e7ed;
    box-shadow: 0 4px 24px rgba(15,17,23,0.06), 0 1px 4px rgba(15,17,23,0.04);
    flex-shrink: 0;
  }
  .gsw-btn {
    flex: 1;
    border: 0;
    background: transparent;
    border-radius: 8px;
    padding: 6px 4px;
    font-size: 11px;
    font-weight: 700;
    color: #5a6175;
    cursor: pointer;
    line-height: 1.2;
  }
  .gsw-btn.is-on {
    background: #1975d1;
    color: #fff;
  }
  .gsw-btn:disabled { opacity: 0.55; cursor: default; }
`;

export default function Dashboard() {
  const { role } = useAuth();
  const isAdmin = hasRoleAccess(role, ACCESS.ADMIN_ONLY);
  const [rankingKey, setRankingKey] = useState(0);
  const [mode, setMode] = useState("schedule");
  const [clockGame, setClockGame] = useState(scheduledGame);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const tick = () => setClockGame(scheduledGame());
    tick();
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => {
      gameScoreService
        .getActive()
        .then((res) => {
          if (!alive) return;
          const next = res.data?.data?.game;
          if (next === "memorama" || next === "cockroach" || next === "schedule") {
            setMode(next);
          }
        })
        .catch(() => {});
    };
    load();
    const id = window.setInterval(load, 15000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);

  const game = mode === "schedule" ? clockGame : mode;

  const pickGame = async (next) => {
    if (!isAdmin || next === mode) return;
    setSaving(true);
    const prev = mode;
    setMode(next);
    try {
      await gameScoreService.setActive(next);
    } catch {
      setMode(prev);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          "html, body": {
            scrollbarColor: `${THEME_COLORS.primary} ${THEME_COLORS.background}`,
            scrollbarWidth: "thin",
          },
          "*::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: THEME_COLORS.primary,
            borderRadius: 8,
          },
        }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: 2,
          width: "100%",
          minWidth: 0,
          flex: 1,
          minHeight: 0,
          height: "calc(100dvh - var(--app-nav-height, 56px) - (var(--app-page-padding, 24px) * 2))",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flex: "1 1 520px", minWidth: 0, minHeight: 0, display: "flex" }}>
          <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
            <ServicesByTechnicianDetailedChart />
          </Box>
        </Box>

        <Box
          sx={{
            width: 320,
            flex: "0 0 320px",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            minHeight: 0,
            minWidth: 280,
            overflow: "hidden",
          }}
        >
          {isAdmin && (
            <div className="gsw">
              <style>{SWITCH_STYLES}</style>
              {[
                { id: "schedule", label: "Horario" },
                { id: "memorama", label: "Lotería" },
                { id: "cockroach", label: "Cucarachas" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`gsw-btn${mode === opt.id ? " is-on" : ""}`}
                  disabled={saving}
                  onClick={() => pickGame(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          {game === "memorama" ? (
            <PestMemorama onGameEnd={() => setRankingKey((n) => n + 1)} />
          ) : (
            <CockroachGame onGameEnd={() => setRankingKey((n) => n + 1)} />
          )}
          <CockroachRanking refreshKey={rankingKey} game={game} />
          <PestHotBranches />
        </Box>
      </Box>
    </>
  );
}
